import { Component } from '@theme/component';

/**
 * @typedef {Object} CustomRingCarouselRefs
 * @property {HTMLElement} viewport - The clipping viewport
 * @property {HTMLElement} track - The flex track that translates
 * @property {HTMLElement[]} slides - The card slides
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/**
 * Ring gallery carousel — centers the active card within the viewport, fades the
 * outer cards, and steps with the arrow buttons. Figma 338:5648.
 * @extends {Component<CustomRingCarouselRefs>}
 */
class CustomRingCarousel extends Component {
  #current = 0;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {AbortController | undefined} */
  #swipeController;
  /** Minimum horizontal travel (px) to register as a swipe */
  #swipeThreshold = 40;
  #pointerStartX = 0;
  #pointerStartY = 0;
  #pointerId = null;
  /** True once a gesture is confirmed horizontal and the track is following the finger */
  #dragging = false;
  /** Track offset (px) when the current drag started */
  #dragBaseOffset = 0;
  /** #current when the drag started */
  #dragBaseIndex = 0;
  /** Last offset applied to the track, so a drag can pick up from the resting position */
  #currentOffset = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#current = this.#clamp(Number(this.dataset.startIndex) || 0);
    this.#resizeObserver = new ResizeObserver(() => this.#render(false));
    this.#resizeObserver.observe(this);
    this.#bindSwipe();
    this.#render(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#swipeController?.abort();
  }

  /** Bind touch/pointer swipe gestures on the viewport so mobile users can drag between cards. */
  #bindSwipe() {
    const { viewport } = this.refs;
    if (!viewport) return;

    this.#swipeController = new AbortController();
    const { signal } = this.#swipeController;

    viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    viewport.addEventListener('pointermove', this.#onPointerMove, { signal });
    viewport.addEventListener('pointerup', this.#onPointerUp, { signal });
    viewport.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    // Swallow the synthetic click that follows a drag so the card link doesn't navigate
    viewport.addEventListener('click', this.#onClick, { signal, capture: true });
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerId = event.pointerId;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    this.#dragBaseOffset = this.#currentOffset;
    this.#dragBaseIndex = this.#current;
    this.#dragging = false;
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#pointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.#pointerStartX;
    const deltaY = event.clientY - this.#pointerStartY;

    if (!this.#dragging) {
      // Wait until the gesture is clearly horizontal before hijacking it from page scroll
      if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) < 6) return;
      this.#dragging = true;
      const { track, viewport } = this.refs;
      track.style.transition = 'none';
      viewport.setPointerCapture(event.pointerId);
    }

    // Track follows the finger 1:1; dragging left (negative deltaX) increases the offset
    this.#applyOffset(this.#dragBaseOffset - deltaX);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.#pointerId = null;
    if (!this.#dragging) return;
    this.#dragging = false;

    const moved = event.clientX - this.#pointerStartX;
    const step = this.#stepWidth();
    // Snap to the nearest card, but honour a small flick past the threshold
    let indexDelta = step > 0 ? Math.round(-moved / step) : 0;
    if (indexDelta === 0 && Math.abs(moved) >= this.#swipeThreshold) {
      indexDelta = moved < 0 ? 1 : -1;
    }

    this.#current = this.#clamp(this.#dragBaseIndex + indexDelta);
    this.#render(true);
  };

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.#pointerId = null;
    if (!this.#dragging) return;
    this.#dragging = false;
    this.#render(true);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // A tap navigates; a drag must not. Suppress the link click only when the finger travelled.
    const moved = Math.abs(event.clientX - this.#pointerStartX);
    if (moved < this.#swipeThreshold) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** Horizontal distance between adjacent cards (card width + gap). */
  #stepWidth() {
    const { slides } = this.refs;
    if (!slides || slides.length === 0) return 0;
    if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
    return slides[0].offsetWidth;
  }

  /** Clamp a raw offset to the valid scroll range and apply it to the track immediately. */
  #applyOffset(offset) {
    const { track, viewport } = this.refs;
    if (!track || !viewport) return;
    const maxOffset = Math.max(0, track.scrollWidth - viewport.clientWidth);
    const clamped = Math.max(0, Math.min(offset, maxOffset));
    this.#currentOffset = clamped;
    track.style.transform = `translate3d(${-clamped}px, 0, 0)`;
  }

  previous() {
    this.#current = this.#wrap(this.#current - 1);
    this.#render(true);
  }

  next() {
    this.#current = this.#wrap(this.#current + 1);
    this.#render(true);
  }

  get #count() {
    return this.refs.slides?.length ?? 0;
  }

  #clamp(index) {
    const count = this.#count;
    if (count === 0) return 0;
    return Math.max(0, Math.min(index, count - 1));
  }

  /** Wrap an index around the slide count so next()/previous() loop past either end. */
  #wrap(index) {
    const count = this.#count;
    if (count === 0) return 0;
    return ((index % count) + count) % count;
  }

  /**
   * @param {boolean} animate - Whether to animate the translation
   */
  #render(animate) {
    const { track, viewport, slides } = this.refs;
    const count = slides?.length ?? 0;
    if (!track || !viewport || count === 0) return;

    const active = slides[this.#current];
    if (!active) return;

    // Spread each index evenly across the available scroll range instead of centering
    // the active card directly: centering clamps several indices near either end to the
    // same offset (there isn't room to center them without revealing empty edges), so
    // clicking next/previous through those indices only changed which card was
    // highlighted, not the card's position. This guarantees every index maps to a
    // distinct offset, so every click visibly moves the track.
    const maxOffset = Math.max(0, track.scrollWidth - viewport.clientWidth);
    const offset = count > 1 ? (this.#current / (count - 1)) * maxOffset : 0;
    this.#currentOffset = offset;

    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    if (!animate) {
      // Restore the CSS-defined transition on the next frame
      requestAnimationFrame(() => {
        track.style.transition = '';
      });
    }

    slides.forEach((slide, index) => {
      const distance = Math.abs(index - this.#current);
      slide.dataset.state = distance <= 1 ? 'near' : 'far';
      if (index === this.#current) slide.dataset.state = 'active';
    });
  }
}

if (!customElements.get('custom-ring-carousel')) {
  customElements.define('custom-ring-carousel', CustomRingCarousel);
}
